import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { StateValues } from 'react-use-form-state';
import { AdminApi, AdminUserCreationRequest, DropdownElement, OrganizationPermissionsEnum } from 'api';
import useApiCall from 'hooks/use-api-call';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons, makeModalForm } from 'ui/molecules/modal';
import Tooltip, { TooltipPosition } from 'src/ui/atoms/tooltip';
import useIsMedia, { DEVICES } from 'src/ui/hooks/use-is-media';
import Icon from 'src/ui/atoms/icon';
import { debounce } from 'lodash';
import { handleError } from 'src/ui/helper/error-handling';
import useTranslate from 'src/ui/hooks/use-translate';
import { currentUserHasManageUsersAndGroupsPermission } from 'src/apps/issuer/helpers/userPermissions';
import { useCurrentUserSelector } from 'src/core/auth/hooks';

interface AddUserModalProps {
  onSaveUser: () => void;
  onClose: () => void;
}

interface AddUserFields {
  email: string;
  organization: string;
}

export const Form = makeModalForm<AddUserFields>();

const AddUserModal: FunctionComponent<AddUserModalProps> = ({ onSaveUser, onClose }) => {
  const { makeAuthenticatedApi, withApi, error } = useApiCall();
  const [organizations, setOrganizations] = useState<DropdownElement[]>([]);
  const { currentUser } = useCurrentUserSelector();

  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const onSubmit = useCallback((fieldsValues: StateValues<AddUserFields>) => {
    const params: AdminUserCreationRequest = {
      email: fieldsValues.email,
      organization: fieldsValues.organization,
    };

    withApi(async () => {
      await adminApi.adminUsersCreate({ adminUserCreationRequest: params });

      onSaveUser();
    });
  }, []);

  const isPhone = useIsMedia(DEVICES.phone);

  const fetchOrganizations = useCallback(
    (searchQuery: string = '', action?: string) => {
      withApi(async () => {
        if (action === 'menu-close' && searchQuery === '') {
          return;
        }
        const response = await adminApi.adminOrganizationsDropdownList({
          search: searchQuery,
          limit: 100,
          ...(currentUserHasManageUsersAndGroupsPermission(currentUser?.permissions || [])
            ? {}
            : { permissions: OrganizationPermissionsEnum.MANAGE_ORGANIZATION_USERS_AND_GROUPS }),
        });
        setOrganizations(response.results || []);
      });
    },
    [withApi, adminApi],
  );

  const { getErrorForField: getUniqueEmailError } = handleError({
    error,
    translate: useTranslate(),
    specialCodes: {
      email_already_used: {
        field: 'email',
        translateKey: 'issuerPermissionsAddUserModal.fields.email.uniqueError',
      },
    },
  });

  const searchOrganizationValue = useCallback(
    (value: string, action?: string) => {
      fetchOrganizations(value, action);
    },
    [fetchOrganizations],
  );

  const debouncedOrganizationSearch = useMemo(() => debounce(searchOrganizationValue, 500), [searchOrganizationValue]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  return (
    <Modal onClose={onClose}>
      <Form
        i18nKey="issuerPermissionsAddUserModal"
        onSubmit={(values: StateValues<AddUserFields>) => {
          onSubmit(values);
        }}
        error={error}
      >
        <ModalHeader>
          <Translate name={'issuerPermissionsAddUserModal.title'} />
        </ModalHeader>
        <ModalContent>
          <>
            <Form.Group
              name="email"
              required={true}
              fieldError={getUniqueEmailError('email')}
              requiredError={<Translate name="issuerPermissionsAddUserModal.fields.email.missingError" />}
              tooltip={
                <Tooltip
                  content={<Translate name="issuerPermissionsAddUserModal.fields.email.tooltip" />}
                  position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                  maxWidth={200}
                >
                  <Icon name="question" circular={true} size="large" color="info" padded="none" />
                </Tooltip>
              }
            >
              <Form.Input />
            </Form.Group>
            <Form.Group
              name="organization"
              required={true}
              tooltip={
                <Tooltip
                  content={<Translate name="issuerPermissionsAddUserModal.fields.organization.tooltip" />}
                  position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                  maxWidth={200}
                >
                  <Icon name="question" circular={true} size="large" color="info" padded="none" />
                </Tooltip>
              }
            >
              <Form.Select
                options={organizations.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
                onInputChange={debouncedOrganizationSearch}
              />
            </Form.Group>
          </>
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons
            actionButtons={[
              {
                name: 'cancel',
                content: <Translate name="common.cancel" />,
                size: 'large',
                onClick: () => onClose(),
              },
              {
                name: 'send',
                content: <Translate name="common.add" />,
                variant: 'primary',
                type: 'submit',
                size: 'large',
              },
            ]}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddUserModal;
